// ── Giving: what a donor sees of their donations ─────────────────────────────
//
// These types are the contract the user-portal backend serves. Every JSON response is wrapped
// in the usual envelope (`{ success, status, data }`), errors in `{ success: false, status,
// error: { code, message, meta } }`. Money is a number in major units. Dates are ISO-8601.

/**
 * A donation's payment: `processing` until the gateway confirms it; `failed` when the donation
 * was recorded but its charge did not go through, so the donor was not charged for it.
 */
export type DonationStatus = 'processing' | 'paid' | 'failed';

export interface DonationCharity {
  id: string;
  name: string;
  /** ROS or company registration number; empty when the charity has none on file. */
  registration_no: string;
  /**
   * The charity is an approved institution under s44(6) of the Income Tax Act 1967 (as frozen
   * on the donation). Drives the "Tax-deductible" / "Not tax-deductible" tag only; whether the
   * receipt is a tax receipt for this donor is `deductible_for_donor`.
   */
  tax_deductible: boolean;
}

/**
 * The donation a checkout carried, on that checkout's paying charge (`GET /transactions/:id`
 * `donation`). Absent or null when there is none, or when it was refused before any donation
 * was recorded. Only a `paid` donation counts towards what the checkout charged.
 */
export interface TransactionDonation {
  /** `DN-YYMMDD-NNNN`; also the receipt number. */
  donation_ref: string;
  /**
   * The donation payment's own transaction ID: the paid charge, or the attempt that failed.
   * null while processing, and for a failed donation that never reached a charge.
   */
  transaction_id: string | null;
  amount: number;
  currency: string;
  status: DonationStatus;
  charity: DonationCharity;
  /**
   * The receipt is a tax receipt for this donor: the charity is tax-deductible and the donor's
   * nationality has a deductible receipt rule. A tax receipt needs the donor's ID number.
   */
  deductible_for_donor: boolean;
}

/** One paid donation in a list. Opening it shows the checkout it was made in. */
export interface DonationLine {
  donation_ref: string;
  amount: number;
  currency: string;
  /** The paying charge's success time. */
  date: string;
  /** The collecting store. */
  store_name: string;
  /**
   * The transaction ID of the checkout's paying charge: the transaction details route. null
   * when the donation has no checkout to open.
   */
  order_activity_id: string | null;
  /** As on `TransactionDonation`. */
  deductible_for_donor: boolean;
}

export interface CharityYearSummary {
  /**
   * Unique within the year's list. A charity that took donations in two currencies has a row
   * for each, under the same charity id.
   */
  row_key: string;
  charity: DonationCharity & {
    /** The charity's primary cause; null when it has none on file. */
    cause: string | null;
  };
  /** This row's currency. */
  currency: string;
  donation_count: number;
  total: number;
  /** The charity's latest three donations in the year, newest first. */
  latest: DonationLine[];
}

/** One currency's figures in a year. `tax_deductible_total` follows `deductible_for_donor`. */
export interface DonationCurrencyTotal {
  currency: string;
  total: number;
  tax_deductible_total: number;
  not_deductible_total: number;
}

/** `GET /donations/years/:year`: paid donations only. */
export interface DonationYearSummary {
  year: number;
  /** One per currency the donor gave in that year, the one with most donations first; one in practice. */
  totals: DonationCurrencyTotal[];
  donation_count: number;
  charity_count: number;
  /** The first and last donation dates in the year. */
  period_from: string;
  period_to: string;
  /** Grouped by currency, largest total first within it; the screen keeps this order. */
  charities: CharityYearSummary[];
}

/** A month's figures for one charity: every donation that month, not just the loaded rows. */
export interface DonationMonthTotals {
  /** `YYYY-MM` */
  month: string;
  donation_count: number;
  total: number;
}

/** `GET /donations/charities/:charityId/donations?year=&page=&search=`: 7 a page, newest first. */
export interface CharityDonationsPage {
  items: DonationLine[];
  /** Every donation matching the search, across all pages. */
  total_count: number;
  next_page: number | null;
}

/** `GET /donations/charities/:charityId?year=` */
export interface CharityYearDetail {
  charity: DonationCharity;
  year: number;
  currency: string;
  donation_count: number;
  total: number;
  months: DonationMonthTotals[];
}

/** A merchant whose stores take donations at checkout, in the donor's market. */
export interface ParticipatingMerchant {
  merchant_id: string;
  name: string;
  /** null when the merchant has no logo on file. */
  logo_url: string | null;
}

/**
 * `GET /donations/participating-merchants`: up to five merchants where the donor can give at
 * checkout, a different set each day.
 */
export interface ParticipatingMerchants {
  merchants: ParticipatingMerchant[];
}

/** The ID a donor's nationality needs on a tax receipt. */
export interface DonorIdRule {
  /** The field label the sheet shows, e.g. "IC number (MyKad)". */
  label: string;
  /** How many digits the ID has, e.g. 12. */
  digits: number;
}

/** `GET /donations/tax-profile`: what a receipt needs from the donor beyond their donations. */
export interface DonorTaxProfile {
  has_ic_number: boolean;
  /** The saved ID's last four digits, shown as `•••• 5678`; null when none is saved. */
  ic_last4: string | null;
  /** null when the donor's nationality has no tax receipt rule: no ID is ever asked for. */
  id_rule: DonorIdRule | null;
  name_on_file: boolean;
  email_on_file: boolean;
}

/** `PUT /donations/tax-identity`; answers the updated `DonorTaxProfile`. */
export interface SaveIcNumberPayload {
  /** Digits only, no dashes. */
  ic_number: string;
  /** Agreed to print name, IC and email on receipts and share them with the charity. */
  consent: true;
  /**
   * The version of the consent text the donor ticked. The text lives in the portal, so the
   * portal names the version it showed; the backend refuses one that is not current.
   */
  consent_text_version: number;
}

/**
 * Error codes the donation and receipt routes answer with. Downloads (`*.pdf`) answer 409 with
 * one of the first three, 422 `DOCUMENT_TOO_LARGE` when a bundle or summary is over its cap,
 * 429 past the per-user download limit and 503 when the renderer is full.
 */
export const DONATION_ERROR_CODES = {
  /** A tax receipt for this donor needs an ID number and none is saved. */
  DONOR_ID_REQUIRED: 'DONOR_ID_REQUIRED',
  DONOR_NAME_REQUIRED: 'DONOR_NAME_REQUIRED',
  DONOR_EMAIL_REQUIRED: 'DONOR_EMAIL_REQUIRED',
  /** Over 500 receipts in one charity's bundle, or 5,000 rows in a summary. */
  DOCUMENT_TOO_LARGE: 'DOCUMENT_TOO_LARGE',
  /** `PUT /donations/tax-identity`: a different ID is already saved. */
  DONOR_ID_EXISTS: 'DONOR_ID_EXISTS',
  /** `PUT /donations/tax-identity`: `consent_text_version` is not the current one. */
  CONSENT_TEXT_OUTDATED: 'CONSENT_TEXT_OUTDATED',
  /** `PUT /donations/tax-identity`: the donor's nationality has no ID rule, so no ID is kept. */
  DONOR_ID_NOT_APPLICABLE: 'DONOR_ID_NOT_APPLICABLE'
} as const;

export type DonationErrorCode =
  (typeof DONATION_ERROR_CODES)[keyof typeof DONATION_ERROR_CODES];
